import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import type { PerguntaRespondida } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { QuestionarioDaConversa } from '@/pages/secretario/QuestionarioDaConversa';

const MAX_FICHA = 5;

function temResposta(p: PerguntaRespondida): boolean {
  return (p.resposta ?? '').trim() !== '';
}

/** A primeira pergunta do questionário da base pede o relato ("Resuma…"). */
function ehRelato(p: PerguntaRespondida): boolean {
  return /^resum/i.test(p.texto.trim());
}

/**
 * O caso em cinco linhas: o relato do cliente, entre aspas e nas palavras dele,
 * e a ficha. Na ficha vêm primeiro as obrigatórias sem resposta (é o que o
 * advogado precisa buscar), depois as respostas; nunca mais de cinco linhas. O
 * resto atrás de "ver as N perguntas". Nada aqui foi escrito por modelo.
 */
export function Caso({ questionario }: { questionario: PerguntaRespondida[] }) {
  const [aberto, setAberto] = useState(false);
  const relato = questionario.find((p) => ehRelato(p) && temResposta(p));
  const semResposta = questionario.filter((p) => !p.removida && !temResposta(p) && p.obrigatoria && !p.condicao);
  const respondidas = questionario
    .filter((p) => !p.removida && p !== relato && temResposta(p))
    .sort((a, b) => Number(b.obrigatoria && !b.condicao) - Number(a.obrigatoria && !a.condicao));
  const ficha = [...semResposta, ...respondidas].slice(0, MAX_FICHA);

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">O caso</h2>
        {questionario.length === 0 ? (
          <p className="text-sm text-muted-foreground">A entrevista não chegou a acontecer nesta conversa.</p>
        ) : (
          <>
            {relato ? (
              <blockquote className="rounded-r-md border-l-2 bg-muted/60 px-3 py-2 text-sm">
                “{(relato.resposta ?? '').trim()}”
              </blockquote>
            ) : (
              <p className="text-sm text-muted-foreground">Sem relato registrado.</p>
            )}
            {ficha.length > 0 && (
              <dl className="grid grid-cols-[minmax(6rem,38%)_1fr] gap-x-3 gap-y-1.5 text-sm">
                {ficha.map((p) => (
                  <RespostaDaFicha key={p.texto} pergunta={p} />
                ))}
              </dl>
            )}
            <Button
              type="button"
              variant="link"
              className="h-auto min-h-11 px-0 text-sm sm:min-h-0"
              aria-expanded={aberto}
              onClick={() => setAberto((v) => !v)}
            >
              <ChevronRight className={`mr-1 h-4 w-4 transition-transform ${aberto ? 'rotate-90' : ''}`} aria-hidden="true" />
              {aberto ? 'ocultar as perguntas' : `ver as ${questionario.length} perguntas`}
            </Button>
            {aberto && <QuestionarioDaConversa questionario={questionario} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RespostaDaFicha({ pergunta }: { pergunta: PerguntaRespondida }) {
  const resposta = (pergunta.resposta ?? '').trim();
  return (
    <>
      <dt className="text-xs text-muted-foreground">{rotuloCurto(pergunta.texto)}</dt>
      <dd className={resposta ? '' : 'italic text-muted-foreground'}>{resposta || 'Sem resposta'}</dd>
    </>
  );
}

/** "Qual o nome da empresa em que você trabalhou? (como aparece…)" → "Nome da empresa". */
function rotuloCurto(texto: string): string {
  let t = texto.replace(/\s*\(.*?\)\s*/g, ' ').replace(/\?.*$/, '').trim();
  t = t.replace(/^(qual|quais|como|quando|onde|quanto|quantos|quantas|houve|foi|tem|há|o que|em que)\s+(é|era|o|a|os|as|foi|era a|o seu|a sua|seu|sua)?\s*/i, '');
  t = t.replace(/\s+(você|voce|em que você trabalhou|do seu|da sua)\b.*$/i, '');
  t = t.trim();
  if (!t) return texto;
  return t.charAt(0).toUpperCase() + t.slice(1);
}
